import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Check if we're on a product detail page
  const isProductPage = location.pathname.startsWith('/product/');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // If we're on a different page, navigate to home first
    if (window.location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const products = [
    { id: 1, name: t.products.product1.name },
    { id: 2, name: t.products.product2.name },
    { id: 3, name: t.products.product3.name }
  ];

  const navItems = [
    { label: t.nav.home, id: 'home', type: 'scroll' },
    { label: t.nav.products, id: 'products', type: 'dropdown' },
    { label: t.nav.about, id: 'about', type: 'scroll' },
    { label: t.nav.services, id: 'services', type: 'scroll' },
    { label: t.nav.contact, id: 'contact', type: 'scroll' }
  ];

  return (
    <header
      className={`fixed top-0 ${isRTL ? 'right-0' : 'left-0'} ${isRTL ? 'left-0' : 'right-0'} z-50 transition-all duration-300 ${
        isScrolled || isProductPage
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer" onClick={() => scrollToSection('home')}>
            <img
              src="/images/logo.jpg"
              alt="Meskim"
              className="h-12 w-auto transition-opacity hover:opacity-80"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center">
            <NavigationMenu>
              <NavigationMenuList className="flex space-x-6">
                {navItems.map((item) => (
                  <NavigationMenuItem key={item.id}>
                    {item.type === 'dropdown' ? (
                      <div className="relative group">
                        <button
                          onClick={() => scrollToSection(item.id)}
                          className={`text-sm font-medium transition-colors hover:text-teal-600 flex items-center gap-1 h-9 px-4 py-2 ${
                            isScrolled || isProductPage ? 'text-gray-700' : 'text-white/90'
                          }`}
                        >
                          {item.label}
                          <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                        </button>
                        <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                          <div className="w-64 p-3 bg-white rounded-lg shadow-lg border">
                            <div className="grid gap-1">
                              {products.map((product) => (
                                <button
                                  key={product.id}
                                  onClick={() => navigate(`/product/${product.id}`)}
                                  className="block w-full text-left rounded-md px-3 py-2 text-sm font-medium leading-none no-underline outline-none transition-colors hover:bg-teal-50 hover:text-teal-700 focus:bg-teal-50 cursor-pointer text-gray-700"
                                >
                                  {product.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => scrollToSection(item.id)}
                        className={`text-sm font-medium transition-colors hover:text-teal-600 ${
                          isScrolled || isProductPage ? 'text-gray-700' : 'text-white/90'
                        }`}
                      >
                        {item.label}
                      </button>
                    )}
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
            <div className="ml-6">
              <LanguageSwitcher variant="header" />
            </div>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-teal-600 hover:bg-teal-700 text-white transition-colors"
            >
              {t.nav.contactBtn}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={isScrolled || isProductPage ? 'text-gray-700' : 'text-white'} size={24} />
            ) : (
              <Menu className={isScrolled || isProductPage ? 'text-gray-700' : 'text-white'} size={24} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t shadow-lg">
          <nav className="container mx-auto px-4 py-4 flex flex-col space-y-3">
            {navItems.map((item) => (
              <div key={item.id}>
                {item.type === 'dropdown' ? (
                  <div>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className="w-full text-left text-gray-700 hover:text-teal-600 py-2 transition-colors font-medium flex items-center justify-between"
                    >
                      {item.label}
                      <ChevronDown size={16} />
                    </button>
                    <div className="pl-4 mt-2 space-y-1">
                      {products.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => {
                            navigate(`/product/${product.id}`);
                            setIsMobileMenuOpen(false);
                          }}
                          className="w-full text-left text-sm text-gray-600 hover:text-teal-600 py-2 transition-colors"
                        >
                          {product.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => scrollToSection(item.id)}
                    className="w-full text-left text-gray-700 hover:text-teal-600 py-2 transition-colors"
                  >
                    {item.label}
                  </button>
                )}
              </div>
            ))}
            <div className="pt-2">
              <LanguageSwitcher />
            </div>
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-teal-600 hover:bg-teal-700 text-white w-full"
            >
              {t.nav.contactBtn}
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;